import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useSkills } from '../../api/capabilities.js';
import { entryText, menuEntries, menuQuery, SLASH_COMMANDS, type SlashCommand, type SlashEntry } from '@dbrain/app-core/slash';

/**
 * The composer's "/" menu (QW-113, QW-1205), the same in a conversation and
 * on the home page: the built-in commands that work there and the skills
 * the user may run in that place, narrowed by fuzzy match and ranked
 * together, best match first. Enter or Tab takes the highlighted entry into
 * the box (a second Enter runs it), the arrows move, Escape empties it.
 */
export function useSlashMenu({
  text,
  setText,
  place,
  enabled,
  keepPrevious = false,
  commands: available = SLASH_COMMANDS,
}: {
  text: string;
  setText: (value: string) => void;
  place: { workspaceId?: string; conversationId?: string };
  enabled: boolean;
  /** show the skills read before (for no place, or another one) while those of this place load */
  keepPrevious?: boolean;
  /** the commands that work here; the home page has no conversation to act on */
  commands?: readonly SlashCommand[];
}) {
  const placeSkills = useSkills(place, enabled, keepPrevious);
  const query = menuQuery(text);
  const skills = useMemo(
    () =>
      (placeSkills.data ?? [])
        .filter((s) => s.userInvocable && s.enabled && !s.shadowedBy && (s.version !== null || s.level === 'builtin'))
        .map((s) => ({ name: s.name, description: s.description ?? '' })),
    [placeSkills.data],
  );
  const entries = useMemo(() => menuEntries(query, available, skills), [query, available, skills]);
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [query]);
  // the skills of this very place are in (or could not be read), not the ones shown while they load
  const settled = placeSkills.isError || (placeSkills.isSuccess && !placeSkills.isPlaceholderData);
  const failed = placeSkills.isError;

  const pick = (entry: SlashEntry | undefined) => {
    if (entry) setText(entryText(entry));
  };
  /** The box after an Enter on `value` that the menu takes, or null when no menu opens for it. */
  const complete = (value: string): string | null => {
    const top = menuEntries(menuQuery(value), available, skills)[0];
    return top ? entryText(top) : null;
  };

  /** The menu's keys; true when the key was the menu's and the composer should ignore it. */
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    const size = entries.length;
    if (size === 0) return false;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index + (event.key === 'ArrowDown' ? 1 : size - 1)) % size);
      return true;
    }
    if ((event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey) {
      event.preventDefault();
      pick(entries[active]);
      return true;
    }
    if (event.key === 'Escape') {
      setText('');
      return true;
    }
    return false;
  };

  return { entries, active, pick, onKeyDown, complete, settled, failed };
}
